import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import * as api from "../../services/api";
import { THEME_VARS, useSiteSettingsRefresh } from "../../hooks/useSiteSettings";

const THEME_PRESETS = [
  { key: "indigo",   label: "Indigo Purple" },
  { key: "teal",     label: "Teal Cyan" },
  { key: "rose",     label: "Rose Pink" },
  { key: "amber",    label: "Amber Orange" },
  { key: "emerald",  label: "Emerald Green" },
  { key: "sky",      label: "Sky Blue" },
];

const TABS = ["Site Info", "Content", "Contact", "Theme", "Operations"] as const;
type Tab = (typeof TABS)[number];

export default function AdminSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const refreshSiteSettings = useSiteSettingsRefresh();
  const [tab, setTab] = useState<Tab>("Site Info");
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate("/");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    api
      .getAdminSettings()
      .then((d: any) => {
        setSettings(d.settings || {});
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load settings.");
        setLoading(false);
      });
  }, []);

  function set(key: string, value: string) {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  function s(key: string, fallback = "") {
    return settings[key] ?? fallback;
  }

  async function save() {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await api.updateAdminSettings(settings);
      refreshSiteSettings();
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (e: any) {
      setError(e.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  }

  if (authLoading || loading) {
    return <div className="p-12 text-center text-gray-400">Loading…</div>;
  }
  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin" className="text-sm text-gray-400 hover:text-gray-600 transition">
              ← Admin
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Manage content, contact info, theme, and site operations — no code changes needed.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="shrink-0 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition disabled:opacity-60 text-sm"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* Feedback */}
      {saved && (
        <div className="mb-6 p-3.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium flex items-center gap-2">
          <span>✓</span> Settings saved successfully.
        </div>
      )}
      {error && (
        <div className="mb-6 p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-8 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition ${
              tab === t
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Site Info ── */}
      {tab === "Site Info" && (
        <div className="space-y-6">
          <Field
            label="Site Name"
            hint="Primary site name used in settings and branding"
            value={s("site_name", "9nd.org")}
            onChange={(v) => set("site_name", v)}
          />
          <Field
            label="Site Tagline"
            hint="Displayed next to the logo"
            value={s("site_tagline", "Powerful Tools, Simplified")}
            onChange={(v) => set("site_tagline", v)}
          />
          <Field
            label="SEO Page Title"
            hint="Browser tab title and search engine result title"
            value={s("seo_title")}
            onChange={(v) => set("seo_title", v)}
          />
          <TextareaField
            label="SEO Description"
            hint="Shown in Google/Bing results — keep under 160 characters"
            rows={3}
            value={s("seo_description")}
            onChange={(v) => set("seo_description", v)}
          />
          <Field
            label="OG Image URL"
            hint="Social share preview image (og:image) — use an absolute URL or /path"
            value={s("og_image_url", "/assets/brand/og-image.png")}
            onChange={(v) => set("og_image_url", v)}
          />
        </div>
      )}

      {/* ── Content ── */}
      {tab === "Content" && (
        <div className="space-y-6">
          <TextareaField
            label="Hero Headline"
            hint="Main heading on the homepage hero section"
            rows={2}
            value={s("hero_headline")}
            onChange={(v) => set("hero_headline", v)}
          />
          <TextareaField
            label="Hero Subtext"
            hint="Supporting paragraph below the main headline"
            rows={3}
            value={s("hero_subtext")}
            onChange={(v) => set("hero_subtext", v)}
          />
          <Field
            label="Primary CTA Button"
            hint='e.g. "Get Started Free"'
            value={s("cta_primary_text", "Get Started Free")}
            onChange={(v) => set("cta_primary_text", v)}
          />
          <Field
            label="Secondary CTA Button"
            hint="e.g. 'Browse All Tools'"
            value={s("cta_secondary_text", "Browse All Tools →")}
            onChange={(v) => set("cta_secondary_text", v)}
          />
          <TextareaField
            label="Footer Tagline"
            hint="Short sentence shown under the logo in the footer"
            rows={2}
            value={s("footer_tagline")}
            onChange={(v) => set("footer_tagline", v)}
          />
        </div>
      )}

      {/* ── Contact ── */}
      {tab === "Contact" && (
        <div className="space-y-6">
          <Field
            label="Contact Email"
            hint="Shown in footer contact links and contact page"
            type="email"
            value={s("contact_email", "info@9nd.org")}
            onChange={(v) => set("contact_email", v)}
          />
          <Field
            label="Contact Phone"
            hint="Optional — leave blank to hide"
            type="tel"
            value={s("contact_phone")}
            onChange={(v) => set("contact_phone", v)}
          />
          <Field
            label="Office / Mailing Address"
            hint="Optional — leave blank to hide"
            value={s("contact_address")}
            onChange={(v) => set("contact_address", v)}
          />
          <div className="border-t border-gray-100 pt-6">
            <p className="text-sm font-semibold text-gray-700 mb-4">Social Links</p>
            <div className="space-y-5">
              <Field
                label="LinkedIn URL"
                hint="e.g. https://linkedin.com/company/9nd-org"
                value={s("social_linkedin")}
                onChange={(v) => set("social_linkedin", v)}
              />
              <Field
                label="X / Twitter URL"
                hint="e.g. https://twitter.com/9ndorg"
                value={s("social_twitter")}
                onChange={(v) => set("social_twitter", v)}
              />
              <Field
                label="GitHub URL"
                hint="e.g. https://github.com/9nd-org"
                value={s("social_github")}
                onChange={(v) => set("social_github", v)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Theme ── */}
      {tab === "Theme" && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Choose a color theme. The change applies across the site immediately after saving.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {THEME_PRESETS.map((preset) => {
              const t = THEME_VARS[preset.key];
              const active = s("theme_preset", "indigo") === preset.key;
              return (
                <button
                  key={preset.key}
                  onClick={() => set("theme_preset", preset.key)}
                  className={`rounded-xl p-4 border-2 transition text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    active
                      ? "border-indigo-600 shadow-md bg-indigo-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div
                    className="h-10 rounded-lg mb-3"
                    style={{
                      background: `linear-gradient(to right, ${t.from}, ${t.to})`,
                    }}
                  />
                  <p className="text-sm font-semibold text-gray-900">{preset.label}</p>
                  {active && <p className="text-xs text-indigo-600 mt-0.5">✓ Active</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Operations ── */}
      {tab === "Operations" && (
        <div className="space-y-8">
          {/* Maintenance Mode */}
          <div className="p-5 border border-gray-200 rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={s("maintenance_mode") === "true"}
                onChange={(e) =>
                  set("maintenance_mode", e.target.checked ? "true" : "false")
                }
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="font-semibold text-gray-900">Maintenance Mode</span>
            </label>
            <p className="text-sm text-gray-500 mt-2 ml-7">
              Stores the maintenance mode setting. This frontend does not currently show a maintenance page based on this toggle.
            </p>
          </div>

          {/* Site Banner */}
          <div className="p-5 border border-gray-200 rounded-xl space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={s("banner_enabled") === "true"}
                onChange={(e) =>
                  set("banner_enabled", e.target.checked ? "true" : "false")
                }
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="font-semibold text-gray-900">Show Site Banner</span>
            </label>
            <p className="text-sm text-gray-500 ml-7">
              Display a full-width announcement banner at the top of every page.
            </p>
            <Field
              label="Banner Message"
              hint="e.g. 'New tools just launched — See what's new'"
              value={s("banner_message")}
              onChange={(v) => set("banner_message", v)}
            />
          </div>
        </div>
      )}

      {/* Save button (bottom) */}
      <div className="mt-10 flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

// ── Shared field components ──────────────────────────────────────────────────

function Field({
  label,
  hint,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      />
    </div>
  );
}

function TextareaField({
  label,
  hint,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-800 mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y"
      />
    </div>
  );
}
